'use client'

import { Role } from '@/lib/api/common/interfaces/role.interface'
import { AccessibleCompany, UserCompanyAssignment } from '@/lib/api/common/interfaces/user.interface'
import { getAccessibleCompanies } from '@/lib/api/organization/organization-api'
import { getRoles } from '@/lib/api/roles/roles-api'
import { AiUserSeats, createUser, getAiUserSeats, getUser, updateUser, UserCompanyAssignmentInput } from '@/lib/api/users/users-api'
import { formatPhoneNumber, unMask } from '@/lib/form-state/util'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdBadge,
    MdBusiness,
    MdDelete,
    MdEmail,
    MdPeople,
    MdPerson,
    MdPhone,
    MdStar,
    MdStarBorder,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import UserCommissionSection from './components/user-commission-section'
import { toast } from 'react-toastify'

/** Mesmo limite do backend (`AI_INSTRUCTIONS_MAX_LENGTH`): o texto entra em toda chamada da IA. */
const AI_INSTRUCTIONS_MAX_LENGTH = 4000

interface CompanyAssignmentForm {
    companyId: string
    roleId: string
    isDefault: boolean
}

/** Máscara progressiva de CPF (000.000.000-00). */
const formatCPF = (value: string): string => {
    const numbers = value.replace(/\D/g, '')
    if (numbers.length > 11) return value
    return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

type FormUserType = 'COMPANY' | 'EMPLOYEE' | 'CUSTOM' | 'AI'

export default function EmployeesFormPage() {
    const { user, isPartner, isCompany, isMaster } = useAuth()
    // Usuário de IA consome créditos de IA da conta: só quem responde por ela cria.
    const canChooseAiUser = isCompany || isMaster
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id
    const [loading, setLoading] = useState(false)
    const [loadingEmployee, setLoadingEmployee] = useState(false)
    const [roles, setRoles] = useState<Role[]>([])
    const [companies, setCompanies] = useState<AccessibleCompany[]>([])
    const [loadingRoles, setLoadingRoles] = useState(false)
    const [loadingCompanies, setLoadingCompanies] = useState(false)
    // Vagas de usuário de IA do plano. O tipo só é oferecido quando o plano o inclui.
    const [aiUserSeats, setAiUserSeats] = useState<AiUserSeats | null>(null)
    // Quem já é usuário de IA mantém a vaga ao ser editado.
    const [loadedAsAiUser, setLoadedAsAiUser] = useState(false)

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        phone: '',
        cpf: '',
        userType: 'EMPLOYEE' as FormUserType,
        aiInstructions: '',
    })

    const [assignments, setAssignments] = useState<CompanyAssignmentForm[]>([
        { companyId: '', roleId: '', isDefault: true },
    ])

    useEffect(() => {
        loadRoles()
        loadCompanies()
    }, [])

    useEffect(() => {
        if (!canChooseAiUser) return
        getAiUserSeats().then(response => {
            if (response.success && response.result) setAiUserSeats(response.result)
        })
    }, [canChooseAiUser])

    const planIncludesAiUser = !!aiUserSeats && aiUserSeats.limit !== 0
    const hasFreeAiUserSeat =
        !!aiUserSeats &&
        (aiUserSeats.limit === 'unlimited' || aiUserSeats.used < aiUserSeats.limit)

    useEffect(() => {
        if (isEditing && id) {
            loadEmployee()
        }
    }, [id, isEditing])

    const loadRoles = async () => {
        setLoadingRoles(true)
        try {
            const response = await getRoles()
            if (response.success && response.result) {
                const rolesList = Array.isArray(response.result)
                    ? response.result
                    : [response.result]
                setRoles(rolesList)

                if (!isEditing) {
                    const defaultRole = rolesList.find(
                        (r: Role) => r.defaultForType === 'EMPLOYEE'
                    )
                    if (defaultRole) {
                        setAssignments(prev =>
                            prev.map((a, i) =>
                                i === 0 ? { ...a, roleId: String(defaultRole.id) } : a
                            )
                        )
                    }
                }
            }
        } catch (error) {
            console.error('Erro ao carregar perfis de acesso:', error)
        } finally {
            setLoadingRoles(false)
        }
    }

    const loadCompanies = async () => {
        setLoadingCompanies(true)
        try {
            const response = await getAccessibleCompanies()
            if (response.success && response.result) {
                const list = Array.isArray(response.result)
                    ? response.result
                    : [response.result]
                setCompanies(list)

                if (!isEditing && user?.companyId) {
                    setAssignments(prev =>
                        prev.map((a, i) =>
                            i === 0 && !a.companyId
                                ? { ...a, companyId: String(user.companyId) }
                                : a
                        )
                    )
                }
            }
        } catch (error) {
            console.error('Erro ao carregar empresas:', error)
        } finally {
            setLoadingCompanies(false)
        }
    }

    const loadEmployee = async () => {
        if (!id) return

        setLoadingEmployee(true)
        try {
            const response = await getUser(parseInt(id, 10))
            if (response.success) {
                const employee = Array.isArray(response.result)
                    ? response.result[0]
                    : response.result
                if (employee) {
                    setFormData({
                        name: employee.name || '',
                        email: employee.email || '',
                        password: '',
                        phone: formatPhoneNumber(employee.phone),
                        cpf: employee.cpf ? formatCPF(employee.cpf) : '',
                        userType:
                            (employee.userType as FormUserType) || 'EMPLOYEE',
                        aiInstructions: employee.aiInstructions || '',
                    })
                    setLoadedAsAiUser(employee.userType === 'AI')

                    const existing = ((employee.companies || []) as UserCompanyAssignment[])
                        .filter((uc: UserCompanyAssignment) => uc.active !== false)
                    if (existing.length > 0) {
                        setAssignments(
                            existing.map((uc: UserCompanyAssignment) => ({
                                companyId: String(uc.companyId),
                                roleId: String(uc.roleId),
                                isDefault: !!uc.isDefault,
                            }))
                        )
                    }
                } else {
                    toast.error('Usuário não encontrado')
                    navigate('/security/users')
                }
            } else {
                toast.error(response.error || 'Erro ao carregar usuário')
                navigate('/security/users')
            }
        } catch (error) {
            toast.error('Erro ao carregar usuário')
            navigate('/security/users')
        } finally {
            setLoadingEmployee(false)
        }
    }

    const addAssignment = () => {
        setAssignments(prev => [
            ...prev,
            { companyId: '', roleId: '', isDefault: prev.length === 0 },
        ])
    }

    const removeAssignment = (index: number) => {
        setAssignments(prev => {
            if (prev.length <= 1) return prev
            const removed = prev[index]
            const next = prev.filter((_, i) => i !== index)
            // Se removeu a default, marcar a primeira como default
            if (removed.isDefault && next.length > 0) {
                next[0] = { ...next[0], isDefault: true }
            }
            return next
        })
    }

    const updateAssignment = (
        index: number,
        field: keyof CompanyAssignmentForm,
        value: string | boolean
    ) => {
        setAssignments(prev =>
            prev.map((a, i) => (i === index ? { ...a, [field]: value } : a))
        )
    }

    const setDefaultAssignment = (index: number) => {
        setAssignments(prev =>
            prev.map((a, i) => ({ ...a, isDefault: i === index }))
        )
    }

    const validateAssignments = (): string | null => {
        if (assignments.length === 0) {
            return 'Adicione pelo menos uma empresa'
        }

        const seen = new Set<string>()
        for (const a of assignments) {
            if (!a.companyId) return 'Selecione a empresa em todos os vínculos'
            if (!a.roleId) return 'Selecione o perfil de acesso em todos os vínculos'
            if (seen.has(a.companyId)) {
                return 'Não é permitido duplicar a mesma empresa'
            }
            seen.add(a.companyId)
        }

        const defaults = assignments.filter(a => a.isDefault)
        if (defaults.length !== 1) {
            return 'Marque exatamente uma empresa como padrão'
        }

        return null
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }

        if (!formData.name.trim()) {
            toast.warning('Preencha o nome')
            return
        }

        if (!formData.email.trim()) {
            toast.warning('Preencha o email')
            return
        }

        const assignmentError = validateAssignments()
        if (assignmentError) {
            toast.warning(assignmentError)
            return
        }

        // CPF é opcional: só cobra os 11 dígitos de quem começou a preencher, para
        // não gravar documento pela metade. Campo vazio é um estado válido e apaga
        // o CPF que estiver gravado.
        const rawCpf = unMask(formData.cpf)
        if (rawCpf && rawCpf.length !== 11) {
            toast.warning('CPF incompleto. Preencha os 11 dígitos ou deixe o campo vazio.')
            return
        }

        if (!isEditing) {
            if (!formData.password.trim()) {
                toast.warning('Preencha a senha')
                return
            }

            if (formData.password.length < 6) {
                toast.warning('A senha deve ter no mínimo 6 caracteres')
                return
            }
        }

        const companiesPayload: UserCompanyAssignmentInput[] = assignments.map(a => ({
            companyId: parseInt(a.companyId, 10),
            roleId: parseInt(a.roleId, 10),
            isDefault: a.isDefault,
        }))

        setLoading(true)

        try {
            let response

            if (isEditing) {
                response = await updateUser(parseInt(id!, 10), {
                    name: formData.name.trim(),
                    email: formData.email.trim(),
                    phone: unMask(formData.phone) || undefined,
                    // `null` (e não `undefined`) porque campo vazio aqui significa
                    // "apagar o CPF"; `undefined` seria ignorado pelo backend.
                    cpf: rawCpf || null,
                    userType: formData.userType,
                    // `null` apaga; só o usuário de IA tem instruções.
                    aiInstructions:
                        formData.userType === 'AI'
                            ? formData.aiInstructions.trim() || null
                            : null,
                    companies: companiesPayload,
                })
            } else {
                response = await createUser({
                    name: formData.name.trim(),
                    email: formData.email.trim(),
                    password: formData.password,
                    phone: unMask(formData.phone) || undefined,
                    cpf: rawCpf || undefined,
                    userType: formData.userType,
                    aiInstructions:
                        formData.userType === 'AI'
                            ? formData.aiInstructions.trim() || undefined
                            : undefined,
                    companies: companiesPayload,
                })
            }

            if (response.success) {
                toast.success(
                    isEditing
                        ? 'Usuário atualizado com sucesso'
                        : 'Usuário criado com sucesso'
                )
                navigate('/security/users')
            } else {
                toast.error(
                    response.error ||
                    (isEditing
                        ? 'Erro ao atualizar usuário'
                        : 'Erro ao criar usuário')
                )
            }
        } catch (error) {
            toast.error(
                isEditing
                    ? 'Erro ao atualizar usuário'
                    : 'Erro ao criar usuário'
            )
        } finally {
            setLoading(false)
        }
    }

    if (loadingEmployee) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando usuário...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/security/users')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-neutral to-neutral/70 flex items-center justify-center shadow-lg">
                        <MdPeople className="text-2xl text-white" />
                    </div>
                    <h1 className="text-2xl font-bold text-base-content">
                        {isEditing ? 'Editar Usuário' : 'Novo Usuário'}
                    </h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Nome <span className="text-error">*</span>
                        </span>
                    </label>
                    <div className="relative">
                        <MdPerson className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Nome completo"
                            className="input input-bordered w-full pl-10"
                            value={formData.name}
                            onChange={e =>
                                setFormData({
                                    ...formData,
                                    name: e.target.value,
                                })
                            }
                            required
                        />
                    </div>
                </div>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Email <span className="text-error">*</span>
                        </span>
                    </label>
                    <div className="relative">
                        <MdEmail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                        <input
                            type="email"
                            placeholder="email@exemplo.com"
                            className="input input-bordered w-full pl-10"
                            value={formData.email}
                            onChange={e =>
                                setFormData({
                                    ...formData,
                                    email: e.target.value,
                                })
                            }
                            required
                        />
                    </div>
                </div>

                {!isEditing && (
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Senha <span className="text-error">*</span>
                            </span>
                            <span className="label-text-alt">
                                Mínimo 6 caracteres
                            </span>
                        </label>
                        <input
                            type="password"
                            placeholder="Digite a senha"
                            className="input input-bordered w-full"
                            value={formData.password}
                            onChange={e =>
                                setFormData({
                                    ...formData,
                                    password: e.target.value,
                                })
                            }
                            required={!isEditing}
                            minLength={6}
                        />
                    </div>
                )}

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Telefone{' '}
                            <span className="text-gray-400 text-sm">
                                (opcional)
                            </span>
                        </span>
                    </label>
                    <div className="relative">
                        <MdPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                        <input
                            type="tel"
                            placeholder="(00) 00000-0000"
                            className="input input-bordered w-full pl-10"
                            value={formData.phone}
                            maxLength={15}
                            onChange={e =>
                                setFormData({
                                    ...formData,
                                    phone: formatPhoneNumber(e.target.value),
                                })
                            }
                        />
                    </div>
                </div>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            CPF{' '}
                            <span className="text-gray-400 text-sm">
                                (opcional)
                            </span>
                        </span>
                        {isEditing && formData.cpf && (
                            <button
                                type="button"
                                className="label-text-alt link link-hover text-error"
                                onClick={() =>
                                    setFormData({ ...formData, cpf: '' })
                                }
                            >
                                Apagar CPF
                            </button>
                        )}
                    </label>
                    <div className="relative">
                        <MdBadge className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                        <input
                            type="text"
                            placeholder="000.000.000-00"
                            className="input input-bordered w-full pl-10"
                            value={formData.cpf}
                            maxLength={14}
                            onChange={e =>
                                setFormData({
                                    ...formData,
                                    cpf: formatCPF(e.target.value),
                                })
                            }
                        />
                    </div>
                    <label className="label">
                        <span className="label-text-alt text-base-content/60">
                            Deixe em branco para o usuário ficar sem CPF.
                        </span>
                    </label>
                </div>

                {!isPartner && (
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Tipo de Usuário{' '}
                                <span className="text-error">*</span>
                            </span>
                        </label>
                        <select
                            className="select select-bordered w-full"
                            value={formData.userType}
                            onChange={e =>
                                setFormData({
                                    ...formData,
                                    userType: e.target.value as FormUserType,
                                })
                            }
                            required
                        >
                            <option value="EMPLOYEE">Usuário</option>
                            <option value="COMPANY">Empresa</option>
                            <option value="CUSTOM">Personalizado</option>
                            {((canChooseAiUser && planIncludesAiUser) ||
                                formData.userType === 'AI' ||
                                loadedAsAiUser) && (
                                <option
                                    value="AI"
                                    disabled={!hasFreeAiUserSeat && !loadedAsAiUser}
                                >
                                    {hasFreeAiUserSeat || loadedAsAiUser
                                        ? 'Usuário de IA'
                                        : 'Usuário de IA (limite do plano atingido)'}
                                </option>
                            )}
                        </select>
                        {formData.userType === 'AI' && (
                            <p className="mt-2 text-xs text-base-content/60">
                                Um usuário operado por inteligência artificial. Ele entra no
                                sistema como qualquer pessoa e só faz o que o perfil de acesso
                                dele permite, por isso escolha o perfil com cuidado. O consumo
                                é debitado dos créditos de IA da conta.
                            </p>
                        )}
                    </div>
                )}

                {formData.userType === 'AI' && (
                    <div className="form-control w-full">
                        <label className="label" htmlFor="ai-instructions">
                            <span className="label-text">
                                Instruções de comportamento
                            </span>
                        </label>
                        <textarea
                            id="ai-instructions"
                            className="textarea textarea-bordered w-full min-h-36"
                            value={formData.aiInstructions}
                            maxLength={AI_INSTRUCTIONS_MAX_LENGTH}
                            placeholder="Ex.: Use um tom cordial e objetivo. Ofereça sempre as formas de pagamento disponíveis. Se o cliente pedir desconto, passe para uma pessoa da equipe."
                            onChange={e =>
                                setFormData({
                                    ...formData,
                                    aiInstructions: e.target.value,
                                })
                            }
                        />
                        <p className="mt-2 text-xs text-base-content/60">
                            Como a IA deve agir: tom, o que pode oferecer e quando passar
                            para uma pessoa. {formData.aiInstructions.length}/
                            {AI_INSTRUCTIONS_MAX_LENGTH} caracteres.
                        </p>
                    </div>
                )}

                <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-base-content flex items-center gap-2">
                                <MdBusiness className="text-xl" />
                                Acessos por Empresa
                                <span className="text-error">*</span>
                            </h3>
                            <p className="text-sm text-base-content/60">
                                Defina em quais empresas o usuário tem acesso e qual perfil exerce em cada uma. Marque uma como padrão (empresa ativa ao logar).
                            </p>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline btn-secondary gap-1"
                            onClick={addAssignment}
                            disabled={loadingCompanies || loadingRoles}
                        >
                            <MdAdd className="text-lg" />
                            Adicionar empresa
                        </button>
                    </div>

                    <div className="flex flex-col gap-3">
                        {assignments.map((assignment, index) => (
                            <div
                                key={index}
                                className="card bg-base-200/50 border border-base-300"
                            >
                                <div className="card-body p-4 gap-3">
                                    <div className="flex items-center justify-between">
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-sm gap-2"
                                            onClick={() =>
                                                setDefaultAssignment(index)
                                            }
                                            title={
                                                assignment.isDefault
                                                    ? 'Empresa padrão'
                                                    : 'Definir como padrão'
                                            }
                                        >
                                            {assignment.isDefault ? (
                                                <>
                                                    <MdStar className="text-lg text-warning" />
                                                    <span className="text-sm font-medium">
                                                        Padrão
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <MdStarBorder className="text-lg text-base-content/50" />
                                                    <span className="text-sm text-base-content/70">
                                                        Definir padrão
                                                    </span>
                                                </>
                                            )}
                                        </button>
                                        {assignments.length > 1 && (
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm btn-circle text-error"
                                                onClick={() =>
                                                    removeAssignment(index)
                                                }
                                                title="Remover vínculo"
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div className="form-control w-full">
                                            <label className="label py-1">
                                                <span className="label-text text-sm">
                                                    Empresa
                                                </span>
                                            </label>
                                            <select
                                                className="select select-bordered select-sm w-full"
                                                value={assignment.companyId}
                                                onChange={e =>
                                                    updateAssignment(
                                                        index,
                                                        'companyId',
                                                        e.target.value
                                                    )
                                                }
                                                required
                                                disabled={loadingCompanies}
                                            >
                                                <option value="">
                                                    {loadingCompanies
                                                        ? 'Carregando...'
                                                        : 'Selecione'}
                                                </option>
                                                {companies.map(company => (
                                                    <option
                                                        key={company.id}
                                                        value={String(company.id)}
                                                    >
                                                        {company.tradingName ||
                                                            company.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="form-control w-full">
                                            <label className="label py-1">
                                                <span className="label-text text-sm">
                                                    Perfil de Acesso
                                                </span>
                                            </label>
                                            <select
                                                className="select select-bordered select-sm w-full"
                                                value={assignment.roleId}
                                                onChange={e =>
                                                    updateAssignment(
                                                        index,
                                                        'roleId',
                                                        e.target.value
                                                    )
                                                }
                                                required
                                                disabled={loadingRoles}
                                            >
                                                <option value="">
                                                    {loadingRoles
                                                        ? 'Carregando...'
                                                        : 'Selecione'}
                                                </option>
                                                {roles.map(role => (
                                                    <option
                                                        key={role.id}
                                                        value={String(role.id)}
                                                    >
                                                        {role.name}
                                                        {role.defaultForType ===
                                                            'EMPLOYEE'
                                                            ? ' (Padrão)'
                                                            : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Comissão por produtividade. Só na edição: o endpoint é por id, e o
                    usuário ainda não existe na criação. Grava sozinha, por um endpoint
                    com permissão própria (RH ou financeiro) — ver o componente. */}
                {isEditing && id && (
                    <UserCommissionSection
                        userId={parseInt(id, 10)}
                        userName={formData.name}
                    />
                )}

                <div className="flex gap-2 pt-4">
                    <button
                        type="button"
                        className="btn btn-ghost flex-1"
                        onClick={() => navigate('/security/users')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary flex-1"
                        disabled={
                            loading ||
                            !formData.name.trim() ||
                            !formData.email.trim() ||
                            (!isEditing && !formData.password.trim())
                        }
                    >
                        {loading ? (
                            <span className="loading loading-spinner"></span>
                        ) : isEditing ? (
                            'Atualizar'
                        ) : (
                            'Salvar'
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
